import { DARE_NOTES, REACTIONS, type BossResponse, type DareDTO, type DareNote, type DaresResponse, type FanMailResponse, type MonsterDTO } from '@shared/protocol/messages';
import { RULES, skillDef } from '../../combat/rules';
import { validateMonster } from '../../combat/budget';
import { DARE_NOTE_TEXT, REACTION_INFO, bossMonsterId, dareMonsterId, dropDare } from '../../systems/community';
import { monsterArt, monsterToDTO } from '../../systems/monsters';
import { addMemory } from '../../systems/progression';
import { artImg, button, confirmBox, meter, modal, statChip, stickyNote, thumb } from '../../../ui/components';
import { h, setChildren } from '../../../ui/dom';
import { doodleSvg } from '../../../ui/doodleSvg';
import { icon } from '../../../ui/icons';
import { refreshBoss } from '../../../networking/community';
import { language, t, tn } from '../../../i18n';
import type { NotebookCtx } from './tabs';
import { ensureDrawingRules } from '../../../ui/drawingRules';

/** Server messages are English sentences; the Thai dictionary carries them too. */
export function errorText(err: unknown): string {
  if (!(err instanceof Error)) return t('Something went wrong.');
  const msg = err.message.charAt(0).toUpperCase() + err.message.slice(1);
  return t(msg.endsWith('.') || msg.endsWith('!') ? msg : `${msg}.`);
}

function sticker(app: NotebookCtx['app'], name: NonNullable<(typeof REACTION_INFO)[keyof typeof REACTION_INFO]['sticker']>, cls = 'reaction-sticker'): Node | null {
  return artImg(app, `sticker:${name}`, cls, doodleSvg(name, cls));
}

/** A server monster on a paper card: drawing, name, a sub line and stats. */
export function card(ctx: NotebookCtx, m: MonsterDTO, sub: string, cls: string, ...extra: (Node | null)[]): HTMLElement {
  return h(
    'article',
    { class: `doodle-card monster-card tier-${m.tier} ${cls}` },
    thumb(ctx.app, m.drawing, monsterArt(m) ?? 'monster:gum_blob', 84),
    h('h3', null, m.name),
    h('p', { class: 'card-sub' }, sub),
    h('div', { class: 'chips' }, statChip('HP', m.hp), statChip('ATK', m.atk), statChip('DEF', m.def), m.luck ? statChip('LUCK', m.luck) : null),
    h('p', { class: 'skill-line' }, `★ ${t(skillDef(m.skill).name)} · ${t(RULES.personalities.find((p) => p.id === m.personality)?.name ?? '')}`),
    ...extra,
  );
}

function fight(ctx: NotebookCtx, monsterId: string): void {
  const { app } = ctx;
  if (!app.store.hero) {
    app.toast(t('Draw a hero first — monsters need someone to fight!'), 'info');
    ctx.goTab('newhero');
    return;
  }
  if (!app.store.state.monsters.some((m) => m.id === monsterId)) {
    app.toast(t('That monster is not in your notebook yet. Try again in a moment.'), 'info');
    return;
  }
  void app.scenes.go('battle', { mode: 'pve', monsterId }, 'page');
}

/* ------------------------------ Dares ------------------------------------ */

/** Pick one of my drawings and a ready-made note, then dare a friend with it. */
export async function sendDareDialog(ctx: NotebookCtx, friend: { id: string; name: string }, onSent: () => void): Promise<void> {
  const { app } = ctx;
  const mine = app.store.state.monsters.filter((m) => m.source === 'mine' && validateMonster(m).ok);
  if (!mine.length) {
    app.toast(t('Draw a monster first, then dare your friends with it!'), 'info');
    return;
  }
  const pick = h('select', { class: 'hand-select', 'aria-label': t('Monster to send') }, mine.map((m) => h('option', { value: m.id }, m.name)));
  const note = h(
    'select',
    { class: 'hand-select', 'aria-label': t('Note') },
    h('option', { value: '' }, t('(no note)')),
    DARE_NOTES.map((n) => h('option', { value: n }, t(DARE_NOTE_TEXT[n]))),
  );
  const body = h(
    'div',
    { class: 'dare-form' },
    h('p', null, t('{name} can fight it whenever they like. You will see every try and the sticker they send back.', { name: friend.name })),
    h('label', { class: 'field' }, h('span', null, t('Monster')), pick),
    h('label', { class: 'field' }, h('span', null, t('Note')), note),
  );
  const v = await modal(t('Dare {name}', { name: friend.name }), body, [
    { label: t('Cancel'), value: 'no', variant: 'ghost' },
    { label: t('Send dare'), value: 'send', variant: 'primary', color: 'pink' },
  ]);
  if (v !== 'send') return;
  const m = mine.find((x) => x.id === pick.value);
  if (!m) return;
  if (!(await ensureDrawingRules(app))) return;
  try {
    const chosen = note.value as DareNote | '';
    await app.api.sendDare(chosen ? { friendId: friend.id, monster: monsterToDTO(m), note: chosen } : { friendId: friend.id, monster: monsterToDTO(m) });
    app.store.update(['quests', 'memories'], (s) => {
      s.counters.daresSent++;
      addMemory(s, { kind: 'friend', title: t('Dared {name} with {monster}', { name: friend.name, monster: m.name }), text: t('Passed the notebook across the classroom.'), sticker: 'pencil' });
    });
    app.sfx.play('pop');
    app.toast(t('Dare sent to {name}!', { name: friend.name }), 'success');
    onSent();
  } catch (err) {
    app.toast(errorText(err), 'error');
  }
}

const noteLine = (d: DareDTO): HTMLElement | null => (d.note ? h('p', { class: 'dare-note hand-note' }, `“${t(DARE_NOTE_TEXT[d.note])}”`) : null);

/** Dares friends sent me (fight them) and dares I sent (see how my friends did). */
export function daresSection(ctx: NotebookCtx, res: DaresResponse, reload: () => void): HTMLElement {
  const { app } = ctx;
  const remove = async (d: DareDTO, mine: boolean): Promise<void> => {
    const ok = mine
      ? await confirmBox(t('Take the dare back?'), d.status === 'beaten' ? t('Remove {monster} from your sent dares?', { monster: d.monster.name }) : t('{name} will not be able to fight {monster} any more.', { name: d.toName, monster: d.monster.name }), t('Remove'))
      : await confirmBox(t('Put the dare away?'), t('{monster} leaves your notebook. {name} still sees how you did.', { monster: d.monster.name, name: d.fromName }), t('Put away'));
    if (!ok) return;
    try {
      await app.api.removeDare(d.id);
      if (!mine) app.store.update(['monsters'], (s) => dropDare(s, d.id));
    } catch (err) {
      app.toast(errorText(err), 'error');
    }
    reload();
  };

  const incoming = res.incoming.map((d) =>
    card(
      ctx,
      d.monster,
      t('Dare from {name}', { name: d.fromName }),
      `dare-card ${d.status}`,
      d.status === 'beaten' ? h('span', { class: 'stamp green' }, t('BEATEN')) : d.attempts === 0 ? h('span', { class: 'stamp pink' }, t('NEW')) : null,
      noteLine(d),
      d.attempts ? h('p', { class: 'reward-line' }, tn(d.attempts, 'You tried {n} time', 'You tried {n} times')) : null,
      h(
        'div',
        { class: 'card-actions' },
        button(d.status === 'beaten' ? t('Fight again') : t('Fight!'), { small: true, variant: 'primary', color: 'pink', sfx: app, onClick: () => fight(ctx, dareMonsterId(d.id)) }),
        button(t('Put away'), { small: true, variant: 'ghost', sfx: app, onClick: () => void remove(d, false) }),
      ),
    ),
  );

  const status = (d: DareDTO): string =>
    d.status === 'beaten'
      ? tn(d.attempts, 'beaten on try {n}', 'beaten after {n} tries')
      : d.attempts
        ? tn(d.attempts, 'not beaten yet ({n} try)', 'not beaten yet ({n} tries)')
        : t('waiting for {name}', { name: d.toName });
  const outgoing = res.outgoing.map((d) =>
    h(
      'li',
      { class: `friend dare-sent ${d.status}` },
      d.reaction ? sticker(app, REACTION_INFO[d.reaction].sticker, 'sticker') : artImg(app, 'sticker:pencil', 'sticker', doodleSvg('pencil', 'sticker')),
      h('b', null, d.monster.name),
      h(
        'span',
        { class: 'friend-meta' },
        ` → ${d.toName} · ${status(d)}${d.reaction ? ` · ${t('sticker: {sticker}', { sticker: t(REACTION_INFO[d.reaction].label) })}` : ''}`,
      ),
      h('span', { class: 'friend-actions' }, button(d.status === 'beaten' ? t('Remove') : t('Take back'), { small: true, variant: 'ghost', sfx: app, onClick: () => void remove(d, true) })),
    ),
  );

  return h(
    'div',
    { class: 'dares' },
    h('h3', { class: 'section-title' }, icon('star', 'inline-icon'), ` ${t('Dares From Friends')}`),
    incoming.length ? h('div', { class: 'card-grid' }, incoming) : h('p', { class: 'hand-hint' }, t('No dares yet. Dare a friend first, and maybe they will dare you back!')),
    h('h3', { class: 'section-title' }, t('Dares I Sent')),
    outgoing.length ? h('ul', { class: 'friend-list' }, outgoing) : h('p', { class: 'hand-hint' }, t('Press "Dare" next to a friend to send them one of your monsters to beat.')),
  );
}

/* ------------------------------ Fan mail --------------------------------- */

/** Who fought my monsters and which stickers they sent back. */
export function fanMailSection(ctx: NotebookCtx): HTMLElement {
  const { app } = ctx;
  const box = h('section', { class: 'fan-mail' }, h('h3', { class: 'section-title' }, t('Fan Mail')), h('p', { class: 'hand-hint' }, t('Opening the mailbox…')));
  const online = app.store.state.online;
  if (!online) {
    setChildren(box, h('h3', { class: 'section-title' }, t('Fan Mail')), h('p', { class: 'hand-hint' }, t('Sign in and share your monsters to get fan mail.')));
    return box;
  }
  app.api.token = online.token;
  let alive = true;
  ctx.onCleanup(() => (alive = false));
  const render = (res: FanMailResponse): void => {
    const KIND = { share: t('shared'), hall: t('Hall of Fame'), dare: t('dare') };
    const rows = res.monsters.map((m) =>
      h(
        'li',
        { class: 'friend fan-row' },
        artImg(app, 'sticker:heart', 'sticker', doodleSvg('heart', 'sticker')),
        h('b', null, m.name),
        h(
          'span',
          { class: 'friend-meta' },
          ` (${KIND[m.kind]}) · ${tn(m.fans, '{n} fan', '{n} fans')} · ${tn(m.fights, '{n} battle', '{n} battles')} · ${tn(m.monsterWins, 'won {n}', 'won {n}')}`,
        ),
        h(
          'span',
          { class: 'reaction-counts' },
          REACTIONS.filter((r) => (m.reactions[r] ?? 0) > 0).map((r) =>
            h('span', { class: 'reaction-count', title: t(REACTION_INFO[r].label) }, sticker(app, REACTION_INFO[r].sticker, 'reaction-mini'), ` ×${m.reactions[r]}`),
          ),
        ),
      ),
    );
    const notes = res.recent.map((n) =>
      h(
        'li',
        { class: 'fan-note' },
        n.reaction ? sticker(app, REACTION_INFO[n.reaction].sticker, 'reaction-mini') : null,
        ` ${
          n.reaction
            ? t('{name} fought {monster} and sent "{sticker}"', { name: n.fromName, monster: n.monsterName, sticker: t(REACTION_INFO[n.reaction].label) })
            : n.lastResult === 'win'
              ? t('{name} beat {monster}', { name: n.fromName, monster: n.monsterName })
              : t('{name} lost to {monster}', { name: n.fromName, monster: n.monsterName })
        }`,
      ),
    );
    setChildren(
      box,
      h('h3', { class: 'section-title' }, t('Fan Mail')),
      rows.length
        ? h('ul', { class: 'friend-list' }, rows)
        : h('p', { class: 'hand-hint' }, t('Nobody has fought your monsters yet. Share one, dare a friend or win the contest!')),
      notes.length ? h('div', { class: 'sticky-note blue fan-notes' }, h('p', null, h('b', null, t('Latest fans'))), h('ul', null, notes)) : null,
    );
  };
  app.api
    .fanMail()
    .then((res) => alive && render(res))
    .catch(() => alive && setChildren(box, h('h3', { class: 'section-title' }, t('Fan Mail')), h('p', { class: 'hand-hint' }, t('Battle server offline — fan mail is unavailable.'))));
  return box;
}

/* ------------------------------ Weekly boss ------------------------------ */

function bossEnds(iso: string): string {
  return new Date(iso).toLocaleString(language() === 'th' ? 'th-TH' : undefined, { weekday: 'long', day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' });
}

/** The boss of the week: last week's champion, one HP pool for everybody. */
export function bossSection(ctx: NotebookCtx): HTMLElement {
  const { app } = ctx;
  const box = h('section', { class: 'boss-week' });
  const title = (): HTMLElement => h('h3', { class: 'section-title' }, icon('trophy', 'inline-icon'), ` ${t('Boss of the Week')}`);
  if (!app.store.state.online) {
    setChildren(box, title(), h('p', { class: 'hand-hint' }, t('Sign in to help everyone beat the boss of the week.')));
    return box;
  }
  setChildren(box, title(), h('p', { class: 'hand-hint' }, t('Looking for the boss…')));
  let alive = true;
  ctx.onCleanup(() => (alive = false));
  const render = (res: BossResponse): void => {
    const b = res.boss;
    if (!b) {
      setChildren(box, title(), h('p', { class: 'hand-hint' }, t('The first boss appears the week after the first contest champion is crowned.')));
      return;
    }
    const left = Math.max(0, b.poolHp - b.damage);
    setChildren(
      box,
      title(),
      h('p', { class: 'hand-hint' }, t("Last week's champion is back, stronger! Everyone fights the same boss, and every battle chips away at its HP.")),
      h(
        'div',
        { class: 'boss-row' },
        card(
          ctx,
          b.monster,
          t('by {name} · champion of {round}', { name: b.authorName, round: b.fromRound }),
          `boss-card ${b.defeated ? 'defeated' : ''}`,
          b.defeated ? h('span', { class: 'stamp red' }, t('DEFEATED')) : h('span', { class: 'stamp gold' }, t('BOSS')),
          h(
            'div',
            { class: 'card-actions' },
            button(t('Fight the boss!'), {
              small: true,
              variant: 'primary',
              color: 'pink',
              sfx: app,
              disabled: b.hitsLeft === 0,
              title: b.hitsLeft === 0 ? t('You used all your boss battles this week') : undefined,
              onClick: () => fight(ctx, bossMonsterId(res.round)),
            }),
          ),
        ),
        stickyNote(
          b.defeated ? 'green' : 'pink',
          h('p', null, h('b', null, b.defeated ? t('Everyone beat the boss!') : t('Boss HP left: {left} / {pool}', { left, pool: b.poolHp }))),
          meter(left, b.poolHp, 'hp', t('Boss HP')),
          h('p', null, tn(b.fighters, '{n} player has helped so far.', '{n} players have helped so far.')),
          h('p', null, t('My hits: {hits} ({damage} damage) · battles left this week: {left}', { hits: b.myHits, damage: b.myDamage, left: b.hitsLeft })),
          b.mine ? h('p', { class: 'hand-hint' }, t('This is your drawing! Every battle sends you fan mail.')) : null,
          h('p', { class: 'hand-hint' }, t('New boss {when}.', { when: bossEnds(res.endsAt) })),
        ),
      ),
    );
  };
  void refreshBoss(app).then((res) => {
    if (!alive) return;
    if (res) render(res);
    else setChildren(box, title(), h('p', { class: 'hand-hint' }, t('Battle server offline — the boss is unavailable.')));
  });
  return box;
}
